import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Popconfirm,
  App,
  Alert,
  Upload,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import { useSalesOrders, useDeleteSalesOrder, useUpdateSalesOrderStatus } from '../../hooks/useSalesOrder';
import { SalesOrder } from '../../services/salesOrder.service';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AED_CURRENCY_CODE } from '@/constants/uaDirhamDisplay';
import { AedDirhamSign } from '@/components/currency/AedDirhamSign';
import { useSystemSettings } from '@/hooks/useSettings';
import salesOrderService from '@/services/salesOrder.service';
import PrintableSalesOrder from '@/components/sales/PrintableSalesOrder';
import { useExcelMigrationIo } from '@/hooks/useExcelMigrationIo';

const { Title, Text } = Typography;
const { Option } = Select;

const SalesOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { baseCurrency, formatCurrency, convertToBaseCurrency, loading: currencyLoading } = useCurrency();
  const { data: settings } = useSystemSettings();
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('order_status') || '');
  const [teamFilter, setTeamFilter] = useState(() => searchParams.get('sales_team_id') || '');
  const [pendingWorkOrder, setPendingWorkOrder] = useState(
    () => searchParams.get('pending_work_order') === 'true'
  );

  const { isExcelBusy, downloadTemplate, downloadExport, handleImport } = useExcelMigrationIo({
    templateUrl: '/sales/orders/excel/template',
    exportUrl: '/sales/orders/excel/export',
    importUrl: '/sales/orders/excel/import',
    templateFilename: 'sales-orders-import-template.xlsx',
    exportFilenamePrefix: 'sales-orders',
    onImportSuccess: () => queryClient.invalidateQueries({ queryKey: ['sales-orders'] }),
    buildExportParams: () => ({
      search: debouncedSearch || undefined,
      order_status: statusFilter || undefined,
      sales_team_id: teamFilter || undefined,
    }),
  });

  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });

  const {
    data: ordersData,
    isLoading,
    refetch: refetchOrders,
    error: ordersError
  } = useSalesOrders({
    order_status: statusFilter || undefined,
    sales_team_id: teamFilter || undefined,
    pending_work_order: pendingWorkOrder ? 'true' : undefined,
    search: debouncedSearch || undefined,
  });
  
  const deleteOrderMutation = useDeleteSalesOrder();
  const updateStatusMutation = useUpdateSalesOrderStatus();

  const orders: SalesOrder[] = Array.isArray(ordersData) 
    ? ordersData 
    : ((ordersData as any)?.data || []);

  // Log for debugging and show error message
  React.useEffect(() => {
    console.log('Sales Orders Page - Orders data:', {
      ordersCount: orders.length,
      statusFilter,
      isLoading,
      hasError: !!ordersError,
      ordersData: ordersData ? (Array.isArray(ordersData) ? ordersData.length : 'not array') : 'null'
    });
    
    // Show error message if there's an error
    if (ordersError) {
      const errorMessage = (ordersError as any)?.response?.data?.message || (ordersError as any)?.message || 'Failed to load customer orders';
      if (errorMessage.includes('Company ID')) {
        message.error('Unable to load customer orders: Your account needs to be associated with a company. Please contact your administrator.');
      } else {
        message.error(`Failed to load customer orders: ${errorMessage}`);
      }
    }
  }, [orders, statusFilter, isLoading, ordersData, ordersError, message]);

  // Helper function to safely convert to number
  const toNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Calculate statistics
  const totalOrders = orders.length;
  const totalValue = orders.reduce(
    (sum, order) => sum + convertToBaseCurrency(toNumber(order.grand_total), order.currency),
    0
  );
  const inProduction = orders.filter(o => o.order_status === 'In Production').length;
  const delivered = orders.filter(o => o.order_status === 'Delivered').length;

  const handleView = (orderId: string) => {
    navigate(`/growth/orders/${orderId}`);
  };

  const handleEdit = (orderId: string) => {
    navigate(`/growth/orders/edit/${orderId}`);
  };

  const handleDelete = async (order: SalesOrder) => {
    try {
      await deleteOrderMutation.mutateAsync(order.id);
      message.success(`Customer Order ${order.sales_order_number} deleted successfully`);
    } catch (error: any) {
      console.error('Error deleting sales order:', error);
      message.error(error.response?.data?.message || 'Failed to delete customer order');
    }
  };

  const handlePrint = async (order: SalesOrder) => {
    try {
      const fullOrder = await salesOrderService.getById(order.id);
      const companySettings = settings?.filter((s: any) => s.category === 'company') || [];
      const getSetting = (key: string) => companySettings.find((s: any) => s.setting_key === key)?.setting_value ?? '';
      const companyInfo = {
        name: getSetting('company_name') || 'Company Name',
        address: getSetting('company_address') || '',
        phone: getSetting('company_phone') || '',
        email: getSetting('company_email') || '',
        taxNumber: getSetting('tax_registration_number') ? `TRN: ${getSetting('tax_registration_number')}` : '',
      };

      const mainContent = document.getElementById('root');
      if (mainContent) mainContent.style.display = 'none';
      const printContainer = document.createElement('div');
      printContainer.id = 'print-container';
      printContainer.style.display = 'block';
      document.body.appendChild(printContainer);
      const root = ReactDOM.createRoot(printContainer);
      const cleanup = () => {
        root.unmount();
        if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
        if (mainContent) mainContent.style.display = 'block';
        window.removeEventListener('afterprint', cleanup);
      };
      root.render(
        <PrintableSalesOrder
          order={fullOrder}
          companyInfo={companyInfo}
          onClose={cleanup}
        />
      );
      window.addEventListener('afterprint', cleanup);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Failed to load customer order for print');
    }
  };

  const handleConfirmOrder = async (order: SalesOrder) => {
    try {
      await updateStatusMutation.mutateAsync({ id: order.id, status: 'Confirmed' });
      message.success(`Customer Order ${order.sales_order_number} confirmed successfully`);
    } catch (error: any) {
      console.error('Error confirming sales order:', error);
      message.error(error.response?.data?.message || 'Failed to confirm customer order');
    }
  };

  // Status color mapping
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft':
        return 'default';
      case 'Confirmed':
        return 'blue';
      case 'In Production':
        return 'orange';
      case 'Ready for Delivery':
        return 'cyan';
      case 'Delivered':
        return 'green';
      case 'Cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  // Table columns
  const columns: ColumnsType<SalesOrder> = [
    {
      title: 'Order Number',
      dataIndex: 'sales_order_number',
      key: 'sales_order_number',
      fixed: 'left',
      width: 150,
      render: (text: string, record: SalesOrder) => (
        <Button
          type="link"
          onClick={() => handleView(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      )
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 200
    },
    {
      title: 'Sales team',
      dataIndex: 'sales_team_name',
      key: 'sales_team_name',
      width: 160,
      render: (v: string) => v || '—',
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 150,
      render: (text: string) => text || '-'
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      width: 120,
      render: (date: string) => dayjs(date).format('DD MMM YYYY'),
      sorter: (a, b) => dayjs(a.order_date).unix() - dayjs(b.order_date).unix()
    },
    {
      title: 'Required Date',
      dataIndex: 'required_date',
      key: 'required_date',
      width: 120,
      render: (date: string) => date ? dayjs(date).format('DD MMM YYYY') : '-'
    },
    {
      title: 'Status',
      dataIndex: 'order_status',
      key: 'order_status',
      width: 150,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
      filters: [
        { text: 'Draft', value: 'Draft' },
        { text: 'Confirmed', value: 'Confirmed' },
        { text: 'In Production', value: 'In Production' },
        { text: 'Ready for Delivery', value: 'Ready for Delivery' },
        { text: 'Delivered', value: 'Delivered' },
        { text: 'Cancelled', value: 'Cancelled' }
      ],
      onFilter: (value: any, record: SalesOrder) => record.order_status === value
    },
    {
      title: 'Grand Total',
      dataIndex: 'grand_total',
      key: 'grand_total',
      width: 150,
      align: 'right',
      render: (value: number, record: SalesOrder) => (
        <Text strong>{formatCurrency(toNumber(value), record.currency)}</Text>
      ),
      sorter: (a, b) => toNumber(a.grand_total) - toNumber(b.grand_total)
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, record: SalesOrder) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record.id)}
          >
            View
          </Button>
          {record.order_status === 'Draft' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleConfirmOrder(record)}
                loading={updateStatusMutation.isPending}
                style={{ color: '#52c41a' }}
              >
                Confirm
              </Button>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record.id)}
              />
              <Popconfirm
                title="Delete Customer Order"
                description={`Are you sure you want to delete ${record.sales_order_number}?`}
                onConfirm={() => handleDelete(record)}
                okText="Delete"
                okType="danger"
                cancelText="Cancel"
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </>
          )}
          <Button
            type="text"
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(record)}
          />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <ShoppingCartOutlined style={{ marginRight: '8px' }} />
          Customer Orders
        </Title>
        <Text type="secondary">
          Manage and track customer orders from confirmation to delivery
        </Text>
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message="Customer order register"
          description="Use filters and column filters to narrow the list. Open an order number to act (confirm, approve, deliver). Standard invoices are created from delivery notes via Tax Invoices. Table header stays visible while scrolling."
        />
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={totalOrders}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#145A54' }}
              formatter={(val) => Number(val).toLocaleString('en-US')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Tooltip
              title={`Converted to ${baseCurrency?.currency_code ?? 'base currency'} using master exchange rates`}
            >
              <Statistic
                title="Total Value"
                value={currencyLoading ? 0 : totalValue}
                prefix={
                  String(baseCurrency?.currency_code || '').toUpperCase() === AED_CURRENCY_CODE ? (
                    <AedDirhamSign />
                  ) : (
                    baseCurrency?.symbol || '$'
                  )
                }
                precision={2}
                valueStyle={{ color: '#52c41a' }}
                formatter={(val) =>
                  currencyLoading
                    ? '…'
                    : Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
              />
            </Tooltip>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="In Production"
              value={inProduction}
              valueStyle={{ color: '#faad14' }}
              formatter={(val) => Number(val).toLocaleString('en-US')}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Delivered"
              value={delivered}
              valueStyle={{ color: '#52c41a' }}
              formatter={(val) => Number(val).toLocaleString('en-US')}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Table Card */}
      <Card>
        {/* Toolbar */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <Space>
            <Input
              placeholder="Search number, customer, project, customer PO, line description…"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              placeholder="Filter by status"
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value)}
              style={{ width: 180 }}
              allowClear
            >
              <Option value="">All Status</Option>
              <Option value="Draft">Draft</Option>
              <Option value="Confirmed">Confirmed</Option>
              <Option value="In Production">In Production</Option>
              <Option value="Ready for Delivery">Ready for Delivery</Option>
              <Option value="Delivered">Delivered</Option>
              <Option value="Cancelled">Cancelled</Option>
            </Select>
            <Select
              placeholder="Sales team"
              value={teamFilter || undefined}
              onChange={(v) => setTeamFilter(v || '')}
              style={{ width: 180 }}
              allowClear
              options={salesTeams.map((t) => ({ label: t.team_name, value: t.id }))}
            />
            <Tag.CheckableTag checked={pendingWorkOrder} onChange={setPendingWorkOrder}>
              Pending work order
            </Tag.CheckableTag>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                refetchOrders();
                console.log('🔄 Manually refreshing sales orders list');
              }}
              loading={isLoading}
            >
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate} disabled={isExcelBusy}>
              Template
            </Button>
            <Button icon={<DownloadOutlined />} onClick={downloadExport} disabled={isExcelBusy}>
              Export
            </Button>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              customRequest={handleImport}
              disabled={isExcelBusy}
            >
              <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                Import
              </Button>
            </Upload>
          </Space>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/growth/orders/new')}
            >
              New Customer Order
            </Button>
          </Space>
        </div>
        
        {/* Debug info - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}>
            Status Filter: {statusFilter || 'None'} | Orders Count: {orders.length} | Loading: {isLoading ? 'Yes' : 'No'}
          </div>
        )}
        
        {/* Table */}
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={isLoading}
          sticky={{ offsetHeader: 0 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} orders`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          scroll={{ x: 1200, y: 520 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default SalesOrdersPage;
